import { OrderBy, TradesTableQuery } from 'generated/graphql';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Loader, Message } from 'semantic-ui-react';
import { TradesTableView } from './trades-tables-view';

type Props = {
  loading: boolean;
  trades: TradesTableQuery['trade'] | [];
  tradesBis: TradesTableQuery['trade'] | [];
  orderBy: {
    column: string;
    direction: string;
  };
  onChangeBilikZoneBis?: (bilikZoneId: number) => void;
  onSort?: (column: string, direction: string) => void;
  refetch: () => Promise<void>;
  refetchBis: () => Promise<void>;
};
export type Trade = TradesTableQuery['trade'][0] & {
  totalProsCount: number;
  totalProsCountBis: number;
  totalMainTradesProCount: number;
  totalMainTradesProCountBis: number;
};

export const TradesTableContainer: FunctionComponent<Props> = ({
  loading,
  trades,
  tradesBis,
  refetch,
  refetchBis,
  orderBy,
  onSort,
}) => {
  const [loadingBis, setLoadingBis] = useState(false);
  const direction = useMemo(() => {
    switch (orderBy.direction) {
      case 'ascending': {
        return OrderBy.Asc;
      }
      case 'descending': {
        return OrderBy.Desc;
      }
    }
  }, [orderBy.direction]);

  const getMainTradesProCount = (trade: TradesTableQuery['trade'][0]) => {
    return trade.mainTradeProPresentations.reduce(
      (acc, { proPresentation }) =>
        (acc += proPresentation.proViewsAggregate.aggregate?.count || 0),
      0,
    );
  };

  useEffect(() => {
    const fetch = async () => {
      setLoadingBis(true);
      await refetch();
      await refetchBis();
      setLoadingBis(false);
    };
    fetch();
  }, [refetch, refetchBis]);

  const tradesFormatted: Trade[] = trades.map(
    (trade: TradesTableQuery['trade'][0]) => {
      return {
        ...trade,
        totalProsCount: trade.tradeProViewsAggregate.aggregate?.count ?? 0,
        totalProsCountBis:
          tradesBis?.find((tradeBis) => tradeBis.id === trade.id)
            ?.tradeProViewsAggregate.aggregate?.count ?? 0,
        totalMainTradesProCount: getMainTradesProCount(trade) ?? 0,
        totalMainTradesProCountBis:
          getMainTradesProCount(
            tradesBis?.find((tradeBis) => tradeBis.id === trade.id) ?? trade,
          ) ?? 0,
      };
    },
  );
  const [sortedData, setSortedData] = useState<Trade[]>(tradesFormatted);

  useEffect(() => {
    const sortTrades = (trades: Trade[]) => {
      return [...trades].sort((a, b) => {
        switch (orderBy.column) {
          case 'tradeProViews':
            const aCount = a.totalProsCount ?? 0;
            const bCount = b.totalProsCount ?? 0;
            return direction === OrderBy.Asc
              ? aCount - bCount
              : bCount - aCount;
          case 'tradeProViewsBis':
            const aCountBis = a.totalProsCountBis ?? 0;
            const bCountBis = b.totalProsCountBis ?? 0;
            return direction === OrderBy.Asc
              ? aCountBis - bCountBis
              : bCountBis - aCountBis;
          case 'tradeProViewsInMainTradeAndSecondaryTrade':
            const aTotalMainTradesProCount = a.totalMainTradesProCount ?? 0;

            const bTotalMainTradesProCount = b.totalMainTradesProCount ?? 0;

            return direction === OrderBy.Asc
              ? aTotalMainTradesProCount - bTotalMainTradesProCount
              : bTotalMainTradesProCount - aTotalMainTradesProCount;
          case 'tradeProViewsInMainTradeAndSecondaryTradeBis':
            const aTotalMainTradesProCountBis =
              a.totalMainTradesProCountBis ?? 0;

            const bTotalMainTradesProCountBis =
              b.totalMainTradesProCountBis ?? 0;

            return direction === OrderBy.Asc
              ? aTotalMainTradesProCountBis - bTotalMainTradesProCountBis
              : bTotalMainTradesProCountBis - aTotalMainTradesProCountBis;
          case 'name':
            return (
              (direction === OrderBy.Asc
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name)) ?? 0
            );
          case 'tradeGroups':
            const aTradeName = a.tradeGroups[0]?.tradeGroup.name ?? '';
            const bTradeName = b.tradeGroups[0]?.tradeGroup.name ?? '';

            return (
              (direction === OrderBy.Asc
                ? aTradeName.localeCompare(bTradeName)
                : bTradeName.localeCompare(aTradeName)) ?? 0
            );
          default:
            return 0;
        }
      });
    };

    const sortedTrades = sortTrades(tradesFormatted);

    setSortedData(sortedTrades);
  }, [tradesBis, trades, orderBy, direction]);

  if (!loading && !loadingBis && !trades.length) {
    return <Message>Il n&apos;y a pas de résultats</Message>;
  }

  if ((loading || loadingBis) && !trades.length) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  return (
    <TradesTableView
      loading={loading}
      onSort={onSort}
      orderBy={orderBy}
      refetch={refetch}
      trades={sortedData}
    />
  );
};
